/**
 * PROCESO SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con secciones anteriores:
 * - Fondo: #ffffff
 * - Colores: #0b1220, #6b7280, #0b63c6 progresivo
 * - Sin gradientes decorativos
 * - Sin emojis
 * - Animaciones reveal con Intersection Observer
 * - FAQ colapsable
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  MessageCircle,
  Calendar,
  Video,
  FileText,
  CheckCircle2,
  Clock,
  Shield,
  Info,
  ChevronDown,
  Quote
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── TimelineStep Component ───────────────────────────────────────────────────
const TimelineStep = ({ paso, index, isLast, delay }) => {
  const ref = useReveal(delay);
  
  // Opacidades progresivas
  const opacities = ['0.4', '0.5', '0.6', '0.8', '1'];
  const opacity = opacities[index] || '1';

  return (
    <div ref={ref} className="relative flex gap-6">
      {/* Número + Línea conectora */}
      <div className="flex flex-col items-center shrink-0">
        {/* Número */}
        <div 
          className="w-14 h-14 rounded-full flex items-center justify-center border-2 z-10"
          style={{ 
            borderColor: `rgba(11, 99, 198, ${opacity})`,
            backgroundColor: index === 4 ? '#0b63c6' : 'white'
          }}
        >
          <span 
            className="text-[18px] font-bold"
            style={{ color: index === 4 ? 'white' : '#0b63c6' }}
          >
            {paso.numero}
          </span>
        </div>

        {/* Línea conectora */}
        {!isLast && (
          <div 
            className="w-0.5 flex-1 mt-2"
            style={{ backgroundColor: `rgba(11, 99, 198, ${opacity})` }}
          />
        )}
      </div>

      {/* Card del paso */}
      <div className="flex-1 pb-8">
        <div className="bg-white rounded-lg border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_rgba(11,99,198,0.08)] hover:border-[#0b63c6]/40 transition-all duration-300 overflow-hidden">
          
          {/* Header */}
          <div className="flex items-start gap-4 p-5 border-b border-black/[0.06]">
            {/* Icon */}
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `rgba(11, 99, 198, ${parseFloat(opacity) * 0.15})` }}
            >
              {React.cloneElement(paso.icon, { 
                size: 24, 
                strokeWidth: 1.75,
                style: { color: '#0b63c6' }
              })}
            </div>

            {/* Título + Tiempo */}
            <div className="flex-1">
              <h3 className="text-[17px] font-semibold text-[#0b1220] leading-tight mb-1.5">
                {paso.titulo}
              </h3>
              <div className="flex items-center gap-1.5">
                <Clock size={14} strokeWidth={2} className="text-[#6b7280]" />
                <span className="text-[13px] font-medium text-[#6b7280]">
                  {paso.tiempo}
                </span>
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div className="p-5 space-y-3">
            <p className="text-[14px] text-[#6b7280] leading-[1.65]">
              {paso.descripcion}
            </p>

            {/* Detalles */}
            <div className="flex flex-wrap gap-2 pt-2">
              {paso.detalles.map((detalle, idx) => (
                <div
                  key={idx}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200"
                >
                  <CheckCircle2 size={12} strokeWidth={2} className="text-emerald-600" />
                  <span className="text-[12px] font-medium text-emerald-700">
                    {detalle}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── FAQItem Component ────────────────────────────────────────────────────────
const FAQItem = ({ pregunta, respuesta, delay }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useReveal(delay);

  return (
    <div
      ref={ref}
      className="bg-white rounded-lg border border-black/[0.08] shadow-[0_4px_12px_rgba(15,23,42,0.03)] overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-50 transition-colors duration-200"
      >
        <span className="text-[14px] font-semibold text-[#0b1220]">
          {pregunta}
        </span>
        <ChevronDown
          size={18}
          strokeWidth={2}
          className={`text-[#6b7280] shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 pt-1">
          <p className="text-[13.5px] text-[#6b7280] leading-[1.65]">
            {respuesta}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Proceso = () => {
  const headerRef = useReveal(0);
  const garantiasRef = useReveal(0);
  const faqHeaderRef = useReveal(0);
  const testimonialRef = useReveal(0);

  const pasosProceso = [
    {
      numero: "1",
      icon: <Send />,
      titulo: "Llenas el formulario",
      tiempo: "2 minutos",
      descripcion: "Completas tus datos básicos: nombre, empresa, teléfono, email y número de vehículos. Eso es todo.",
      detalles: [
        "Solo lo esencial",
        "Sin tarjeta de crédito",
        "Información segura"
      ]
    },
    {
      numero: "2",
      icon: <MessageCircle />,
      titulo: "Te contactamos",
      tiempo: "Menos de 24 horas",
      descripcion: "Un asesor te escribe por WhatsApp o te llama (según prefieras) para conocer tu caso y agendar la videollamada.",
      detalles: [
        "Respuesta rápida",
        "Canal de tu preferencia",
        "Sin presión"
      ]
    },
    {
      numero: "3",
      icon: <Calendar />,
      titulo: "Agendamos la videollamada",
      tiempo: "A tu conveniencia",
      descripcion: "Eliges el día y hora que mejor te venga. Mañana, tarde o noche. Nos adaptamos a tu agenda.",
      detalles: [
        "Horario flexible",
        "Link de Meet/Zoom",
        "Recordatorio automático"
      ]
    },
    {
      numero: "4",
      icon: <Video />,
      titulo: "Diagnóstico gratuito",
      tiempo: "15 minutos",
      descripcion: "En la videollamada conversamos sobre tu operación, vemos el sistema en vivo, y analizamos si tiene sentido para ti.",
      detalles: [
        "100% virtual",
        "Demo en vivo",
        "Resuelves dudas"
      ]
    },
    {
      numero: "5",
      icon: <FileText />,
      titulo: "Recibes propuesta",
      tiempo: "Mismo día o siguiente",
      descripcion: "Si el sistema es adecuado para tu caso, te enviamos una propuesta personalizada con alcance, inversión y próximos pasos.",
      detalles: [
        "Por escrito",
        "Sin letra chica",
        "Sin presión"
      ]
    }
  ];

  const garantias = [
    {
      icon: <Shield />,
      titulo: "Sin spam ni llamadas molestas",
      descripcion: "Un solo contacto inicial. Si no te interesa, respetamos tu decisión."
    },
    {
      icon: <Clock />,
      titulo: "Respuesta en menos de 24h",
      descripcion: "No te dejamos esperando. Garantizamos respuesta rápida."
    },
    {
      icon: <CheckCircle2 />,
      titulo: "Sin compromiso",
      descripcion: "El diagnóstico es 100% gratuito. No hay obligación de contratar."
    },
    {
      icon: <Info />,
      titulo: "Información clara",
      descripcion: "Te explicamos todo con transparencia. Precios, alcance, tiempos."
    }
  ];

  const faqs = [
    {
      pregunta: "¿Cuánto dura el diagnóstico?",
      respuesta: "15 minutos en promedio. A veces menos si solo tienes dudas puntuales, a veces 20-25 minutos si quieres ver el sistema en más detalle."
    },
    {
      pregunta: "¿Me van a presionar para comprar?",
      respuesta: "No. Si después del diagnóstico vemos que el sistema no es adecuado para tu caso, te lo diremos con honestidad. Solo enviamos propuesta si tiene sentido real."
    },
    {
      pregunta: "¿Puedo cancelar o reprogramar la videollamada?",
      respuesta: "Por supuesto. Entendemos que pueden surgir imprevistos. Solo avísanos con unas horas de anticipación y reagendamos sin problema."
    },
    {
      pregunta: "¿Qué información necesito tener para el diagnóstico?",
      respuesta: "Solo información básica: cuántos vehículos tienes, qué tipo de carga transportas, aproximadamente cuántos viajes al mes. No necesitas números exactos."
    },
    {
      pregunta: "¿Realmente es gratuito?",
      respuesta: "Sí, 100% gratuito y sin compromiso. El diagnóstico es nuestra forma de conocernos mutuamente y ver si podemos ayudarte."
    },
    {
      pregunta: "¿Qué pasa si después no me interesa?",
      respuesta: "Simplemente nos dices que no y listo. No habrá seguimientos insistentes ni llamadas molestas. Respetamos tu decisión."
    }
  ];

  return (
    <section
      id="proceso"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header ref={headerRef} className="max-w-[720px] flex flex-col gap-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 w-fit">
            <Info size={14} strokeWidth={2} className="text-[#0b63c6]" />
            <span className="text-[13px] font-medium text-[#0b63c6]">Transparencia total</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            ¿Qué pasa después de{' '}
            <span className="text-[#0b63c6]">solicitar el diagnóstico?</span>
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            Queremos que sepas{' '}
            <strong className="text-[#0b1220] font-semibold">exactamente qué esperar</strong>. 
            Sin sorpresas, sin spam, sin presión de ventas.
          </p>
        </header>

        {/* ── Timeline de pasos ── */}
        <div className="max-w-[800px]">
          {pasosProceso.map((paso, index) => (
            <TimelineStep
              key={index}
              paso={paso}
              index={index}
              isLast={index === pasosProceso.length - 1}
              delay={index * 80}
            />
          ))}
        </div>

        {/* ── Garantías ── */}
        <div
          ref={garantiasRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 rounded-xl bg-[#f7f9fb] border border-black/[0.08]"
        >
          {garantias.map((garantia, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center">
                {React.cloneElement(garantia.icon, { 
                  size: 20, 
                  strokeWidth: 1.75, 
                  className: 'text-[#0b63c6]' 
                })}
              </div>
              <div>
                <h4 className="text-[13.5px] font-semibold text-[#0b1220] mb-1">
                  {garantia.titulo}
                </h4>
                <p className="text-[12.5px] text-[#6b7280] leading-[1.6]">
                  {garantia.descripcion}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── FAQ ── */}
        <div className="flex flex-col gap-5">
          <h3 ref={faqHeaderRef} className="text-[20px] font-bold text-[#0b1220] text-center">
            Preguntas frecuentes sobre el proceso
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {faqs.map((faq, i) => (
              <FAQItem key={i} {...faq} delay={i * 40} />
            ))}
          </div>
        </div>

        {/* ── Testimonial ── */}
        <div
          ref={testimonialRef}
          className="relative flex flex-col gap-3 p-8 rounded-xl bg-white border border-blue-200 shadow-[0_10px_25px_rgba(11,99,198,0.08)]"
        >
          {/* Quote icon */}
          <div className="absolute top-6 left-6 opacity-10">
            <Quote size={48} strokeWidth={2} className="text-[#0b63c6]" />
          </div>

          {/* Content */}
          <p className="text-[15px] text-[#0b1220] leading-[1.7] italic relative z-10">
            "Me gustó que todo fue transparente desde el inicio. Me contactaron al día siguiente, agendamos para el horario que me convenía, y en la videollamada me mostraron todo sin presión. Al final decidí contratar porque vi que realmente entendían mi negocio."
          </p>

          {/* Autor */}
          <div className="flex items-center gap-2 relative z-10">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <span className="text-[14px] font-bold text-[#0b63c6]">JR</span>
            </div>
            <div>
              <div className="text-[13.5px] font-semibold text-[#0b1220]">
                Jorge Ramírez
              </div>
              <div className="text-[12px] text-[#6b7280]">
                Transportes del Norte
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Proceso;